import React, { useState } from 'react';
import { Phone, Mail, Award, Trophy, Users, ZoomIn } from 'lucide-react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import SectionHeader from '../components/SectionHeader';

const CoachingPage: React.FC = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const galleryImages = [
    { src: '/images/billy/pool/image1.jpg', alt: 'Billy Snaddon coaching' },
    { src: '/images/billy/pool/image2.jpg', alt: 'Billy Snaddon playing pool' },
    { src: '/images/billy/pool/image3.jpg', alt: 'Billy Snaddon coaching session' },
    { src: '/images/billy/snooker/image1.jpg', alt: 'Billy Snaddon at snooker table' },
    { src: '/images/billy/snooker/image2.jpg', alt: 'Billy Snaddon snooker professional' },
  ];

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };
  return (
    <div className="pt-20 bg-primary-black min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <SectionHeader
          eyebrow="PROFESSIONAL COACHING"
          title="Learn from One of the Best"
          subtitle="Former World Top 32 Snooker Professional Billy Snaddon"
          darkMode={true}
        />

        {/* Hero Section with Image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="space-y-6 text-gray-300">
            <p className="text-lg leading-relaxed">
              Our snooker and pool club is proud to offer coaching opportunities with former professional snooker player <span className="text-primary-gold font-semibold">Billy Snaddon</span>, bringing world-class experience directly to our members.
            </p>
            <p className="text-lg leading-relaxed">
              Billy enjoyed a successful professional career spanning <span className="text-white font-semibold">13 years (1991-2004)</span>, spending five years ranked among the world's top 32 players and reaching a career-high ranking of <span className="text-primary-gold font-semibold">24th in the world</span>. Competing at the highest level, he faced and defeated some of the game's biggest names, showcasing the skill, discipline, and tactical knowledge that define elite snooker.
            </p>
            <p className="text-lg leading-relaxed">
              Beyond snooker, Billy is a multiple title-winning pool player. His achievements include double success at the <span className="text-white font-semibold">2016 Blackball International World Championship</span>, winning both the team and seniors individual titles.
            </p>
            <p className="text-lg leading-relaxed">
              Most recently, he led Scotland to victory in the <span className="text-white font-semibold">2026 Nations Cup</span> as player-manager, showcasing both his playing ability and leadership.
            </p>
            <p className="text-lg leading-relaxed">
              Billy's expertise and passion for the game will be a huge asset to our club, and we look forward to the impact he will make.
            </p>
          </div>

          <div 
            className="relative rounded-lg overflow-hidden shadow-2xl cursor-pointer group"
            onClick={() => openLightbox(0)}
          >
            <img
              src="/images/billy/pool/image1.jpg"
              alt="Billy Snaddon coaching"
              className="w-full h-full object-cover min-h-[400px] transition-transform duration-300 group-hover:scale-110"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300">
              <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          </div>
        </div>

        {/* Coaching Description */}
        <div className="mb-16">
          <div className="bg-primary-black rounded-lg p-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
              <div className="lg:col-span-1 flex justify-center">
                <img 
                  src="/images/billy/sightright-logo.png" 
                  alt="SightRight Coaching" 
                  className="w-32 h-32 lg:w-40 lg:h-40 object-contain"
                />
              </div>
              <div className="lg:col-span-4">
                <p className="text-gray-300 text-lg leading-relaxed">
                  Take your snooker and pool game to the next level with Billy Snaddon, a qualified and accredited SightRight coach with a wealth of knowledge and experience. Whether you're a complete beginner looking to learn the fundamentals, an intermediate player aiming to improve consistency, or an experienced player looking to sharpen your technique and performance, Billy offers tailored coaching to suit your individual needs. Using proven coaching methods and SightRight technology, he can help you develop better alignment, accuracy, cueing and confidence, giving you the tools to perform at your best.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Achievements Section */}
        <div className="bg-primary-black border border-primary-gold/20 rounded-lg p-8 mb-16 shadow-2xl">
          <h2 className="text-3xl font-bold text-primary-gold mb-8 font-display text-center">
            Billy's Achievements
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <Award className="w-12 h-12 mx-auto mb-4 text-primary-gold" />
              <h3 className="text-xl font-bold text-white mb-2">13-Year Pro Career</h3>
              <p className="text-gray-300">Professional Snooker Player (1991-2004)</p>
            </div>
            <div className="text-center">
              <Award className="w-12 h-12 mx-auto mb-4 text-primary-gold" />
              <h3 className="text-xl font-bold text-white mb-2">Career High Ranking</h3>
              <p className="text-gray-300">24th in the World - 5 Years in Top 32</p>
            </div>
            <div className="text-center">
              <Trophy className="w-12 h-12 mx-auto mb-4 text-primary-gold" />
              <h3 className="text-xl font-bold text-white mb-2">2016 World Champion</h3>
              <p className="text-gray-300">Blackball International - Team & Seniors Individual</p>
            </div>
            <div className="text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-primary-gold" />
              <h3 className="text-xl font-bold text-white mb-2">2026 Nations Cup</h3>
              <p className="text-gray-300">Led Scotland to Victory as Player-Manager</p>
            </div>
          </div>
        </div>

        {/* What You'll Learn Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary-gold mb-8 font-display text-center">
            What You'll Learn
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-primary-black border border-primary-gold/20 rounded-lg p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-4">Technique Refinement</h3>
              <p className="text-gray-300">
                Master the fundamentals and advanced techniques that have made Billy a world-class player. From stance and grip to cue action and follow-through.
              </p>
            </div>
            <div className="bg-primary-black border border-primary-gold/20 rounded-lg p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-4">Match Play Strategy</h3>
              <p className="text-gray-300">
                Learn tactical thinking, shot selection, and mental game strategies that separate good players from great ones.
              </p>
            </div>
            <div className="bg-primary-black border border-primary-gold/20 rounded-lg p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-4">Game Understanding</h3>
              <p className="text-gray-300">
                Develop a deeper understanding of both snooker and pool, including positional play, safety shots, and break building.
              </p>
            </div>
            <div className="bg-primary-black border border-primary-gold/20 rounded-lg p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-4">All Abilities Welcome</h3>
              <p className="text-gray-300">
                Whether you're a complete beginner or an experienced player looking to take your game further, Billy tailors his coaching to your level.
              </p>
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary-gold mb-8 font-display text-center">
            Billy in Action
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {galleryImages.slice(1).map((image, index) => (
              <div 
                key={index}
                className="relative rounded-lg overflow-hidden shadow-2xl cursor-pointer group"
                onClick={() => openLightbox(index + 1)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300">
                  <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="bg-gradient-to-r from-snooker-green via-primary-black to-snooker-green border border-primary-gold/20 rounded-lg p-12 text-center shadow-2xl">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-gold mb-4 font-display">
            Ready to Improve Your Game?
          </h2>
          <p className="text-gray-300 text-lg mb-6 max-w-2xl mx-auto">
            Now, Billy is sharing that expertise through coaching, helping players of all abilities refine their technique, improve their match play, and develop a deeper understanding of the game.
          </p>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Whether you're looking to take your first steps or elevate your competitive performance, this is a unique opportunity to learn from one of the sport's proven professionals.
          </p>
          <p className="text-white text-xl mb-8 font-semibold">
            For more information or to book coaching sessions, please contact our club today.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="tel:01324636330"
              className="flex items-center gap-2 bg-primary-gold text-primary-black px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary-black hover:text-primary-gold hover:border-2 hover:border-primary-gold transition-all"
              aria-label="Call to book coaching session"
            >
              <Phone className="w-5 h-5" />
              <span>Call: 01324 636330</span>
            </a>
            <a
              href="mailto:enquiries@thecreamerysportsbar.co.uk"
              className="flex items-center gap-2 bg-primary-black text-primary-gold border-2 border-primary-gold px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary-gold hover:text-primary-black transition-all"
              aria-label="Email to inquire about coaching"
            >
              <Mail className="w-5 h-5" />
              <span>Email Us</span>
            </a>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={galleryImages.map(img => ({ src: img.src, alt: img.alt }))}
      />
    </div>
  );
};

export default CoachingPage;
